/**
 * Smart Asset Value Flows Schemas
 *
 * Data-driven schema loading from Smart Asset template markdown files.
 * Schemas are extracted from JSONC code blocks in markdown templates.
 *
 * In production, schemas would be loaded from SA_build_agentics repository.
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/tree/master/docs/SmartAssetSpec/MockSmartAssetTemplates for schema format
 */

import type { ValueFlowsSchema } from "../types/valueFlows";
import {
  getSchema,
  hasSchema,
  registerSchema,
  registerSchemaFromMarkdown,
  loadSchemasFromModules,
  loadSchemasFromAsyncModules,
  clearSchemas,
  getSchemaIds,
  filenameToAssetId,
} from "./schemaLoader";

// Re-export loader functions for external use
export {
  registerSchema,
  registerSchemaFromMarkdown,
  loadSchemasFromModules,
  loadSchemasFromAsyncModules,
  clearSchemas,
  getSchema,
  getSchemaIds,
  filenameToAssetId,
};

/**
 * Get Value Flows schema for a Smart Asset by ID
 *
 * @param assetId - The asset ID to look up (e.g., "sample-smart-treasury")
 * @returns The schema, or undefined if not found
 */
export function getValueFlowsSchema(assetId: string): ValueFlowsSchema | undefined {
  return getSchema(assetId);
}

/**
 * Check if a schema is available for the given asset ID
 *
 * @param assetId - The asset ID to check
 * @returns True if the schema is available
 */
export function hasValueFlowsSchema(assetId: string): boolean {
  return hasSchema(assetId);
}

/**
 * Initialize schemas from raw markdown content
 *
 * This is useful for testing or when markdown content is already loaded.
 *
 * @param schemas - Record of asset IDs to markdown content
 * @returns Number of schemas successfully loaded
 */
export function initializeSchemasFromMarkdown(schemas: Record<string, string>): number {
  let loadedCount = 0;

  for (const [assetId, markdownContent] of Object.entries(schemas)) {
    const schema = registerSchemaFromMarkdown(assetId, markdownContent);
    if (schema) {
      loadedCount++;
    }
  }

  return loadedCount;
}

/**
 * Map of Smart Asset IDs to their Value Flows schemas
 *
 * @deprecated Use getValueFlowsSchema() instead for data-driven access
 */
export const SMART_ASSET_SCHEMAS: Record<string, ValueFlowsSchema> = new Proxy(
  {},
  {
    get(_target, prop: string) {
      return getSchema(prop);
    },
    has(_target, prop: string) {
      return hasSchema(prop);
    },
    ownKeys() {
      return getSchemaIds();
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (hasSchema(prop)) {
        return {
          enumerable: true,
          configurable: true,
          value: getSchema(prop),
        };
      }
      return undefined;
    },
  },
);
